import React from "react";
import { Heart, MessageSquareQuote, Pin, PinOff, Salad, UserRound, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AGE_LABELS, SIDE_LABELS, dietLabel, domainLabel, initials } from "@/lib/format";
import { t, tf } from "@/i18n";

const Line = ({ label, children }) => (
  <div className="border-b border-[color:var(--line)] py-2 last:border-0">
    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--muted-ink)]">
      {label}
    </p>
    <div className="mt-1 text-[13px] leading-snug text-[color:var(--ink)]">{children}</div>
  </div>
);

export const GuestInspector = ({
  guest,
  plan,
  guestMap,
  wishMap,
  avoidMap,
  onTogglePin,
  onClose,
}) => {
  if (!guest) {
    return (
      <section className="surface rounded-xl p-4" data-testid="guest-inspector-empty">
        <h2 className="font-display text-lg font-semibold leading-none">{t("inspector.title")}</h2>
        <p className="mt-2 text-[12.5px] text-[color:var(--ink-soft)]">{t("inspector.hint")}</p>
      </section>
    );
  }

  const party = (plan?.guests || []).filter(
    (other) => other.party_id === guest.party_id && other.id !== guest.id
  );
  const wishes = (wishMap?.[guest.id] || []).map((id) => guestMap[id]?.name).filter(Boolean);
  const avoids = (avoidMap?.[guest.id] || []).map((id) => guestMap[id]?.name).filter(Boolean);

  return (
    <section className="surface rounded-xl p-4" data-testid="guest-inspector">
      <header className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <span
            className="font-num flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-semibold"
            style={{ backgroundColor: "#f2f6f3", color: "var(--sage-dark)" }}
          >
            {initials(guest.name)}
          </span>
          <div>
            <h2
              className="font-display text-lg font-semibold leading-tight"
              data-testid="guest-inspector-name"
            >
              {guest.name}
            </h2>
            <p className="text-[11.5px] text-[color:var(--muted-ink)]">
              {SIDE_LABELS[guest.side] || guest.side}
              {guest.age_band ? ` · ${AGE_LABELS[guest.age_band] || guest.age_band}` : ""}
              {guest.table ? ` · ${tf("inspector.table", { t: guest.table })}` : ` · ${t("inspector.unseated")}`}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onClose}
          data-testid="guest-inspector-close"
          aria-label={t("inspector.close")}
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      {onTogglePin ? (
        <div className="mb-2 flex items-center justify-between gap-2 rounded-lg surface-2 px-2.5 py-2">
          <p className="text-[11.5px] leading-snug text-[color:var(--ink-soft)]">
            {guest.pinned ? tf("inspector.pinnedAt", { t: guest.table }) : t("inspector.freeToMove")}
          </p>
          <Button
            size="sm"
            variant={guest.pinned ? "secondary" : "outline"}
            className="h-7 shrink-0 px-2 text-[11.5px]"
            disabled={!guest.table && !guest.pinned}
            onClick={() => onTogglePin(guest.id, !guest.pinned)}
            data-testid="guest-pin-toggle"
          >
            {guest.pinned ? (
              <>
                <PinOff className="mr-1 h-3.5 w-3.5" /> {t("inspector.unpin")}
              </>
            ) : (
              <>
                <Pin className="mr-1 h-3.5 w-3.5" /> {t("inspector.pinHere")}
              </>
            )}
          </Button>
        </div>
      ) : null}

      {!guest.responded ? (
        <p
          className="mb-2 rounded-lg px-2.5 py-2 text-[12px]"
          style={{ backgroundColor: "#f4f1ec", color: "var(--ink-soft)" }}
        >
          {t("inspector.noAnswer")}
        </p>
      ) : null}

      {guest.answers?.interests ? (
        <Line label={t("inspector.ownWords")}>
          <span className="flex gap-1.5">
            <MessageSquareQuote className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: "var(--gild)" }} />
            <span className="italic">“{guest.answers.interests}”</span>
          </span>
        </Line>
      ) : null}

      {guest.blurb ? <Line label={t("inspector.aiSummary")}>{guest.blurb}</Line> : null}

      {(guest.interest_domains || []).length || (guest.interest_tags || []).length ? (
        <Line label={t("inspector.interests")}>
          <div className="flex flex-wrap gap-1">
            {(guest.interest_domains || []).map((domain) => (
              <Badge key={domain} variant="secondary" className="rounded-full text-[10px] font-semibold">
                {domainLabel(domain)}
              </Badge>
            ))}
            {(guest.interest_tags || []).map((tag) => (
              <span
                key={tag}
                className="rounded-full border px-2 py-0.5 text-[10px]"
                style={{ borderColor: "var(--line)", color: "var(--ink-soft)" }}
              >
                {tag}
              </span>
            ))}
          </div>
        </Line>
      ) : null}

      {(guest.diet_tags || []).length || guest.diet_note ? (
        <Line label={t("inspector.diet")}>
          <span className="flex flex-wrap items-center gap-1.5">
            <Salad className="h-3.5 w-3.5" style={{ color: "var(--success)" }} />
            {(guest.diet_tags || []).map((tag) => (
              <span
                key={tag}
                className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                style={{ backgroundColor: "#f2f6f3", color: "var(--success)" }}
              >
                {dietLabel(tag)}
              </span>
            ))}
            {guest.diet_note ? (
              <span className="text-[12px] text-[color:var(--ink-soft)]">“{guest.diet_note}”</span>
            ) : null}
          </span>
        </Line>
      ) : null}

      {party.length ? (
        <Line label={t("inspector.comesWith")}>
          <span className="flex flex-wrap items-center gap-1.5">
            <UserRound className="h-3.5 w-3.5" style={{ color: "var(--sage-dark)" }} />
            {party.map((member) => member.name).join(", ")}
          </span>
        </Line>
      ) : null}

      {wishes.length || (guest.seat_near || []).length ? (
        <Line label={t("inspector.wantsNear")}>
          <span className="flex flex-wrap items-center gap-1.5">
            <Heart className="h-3.5 w-3.5" style={{ color: "var(--clay)" }} />
            {wishes.length ? wishes.join(", ") : (guest.seat_near || []).join(", ")}
          </span>
        </Line>
      ) : null}

      {avoids.length || (guest.avoid || []).length ? (
        <Line label={t("inspector.mustNot")}>
          <span className="font-semibold" style={{ color: "var(--danger)" }}>
            {avoids.length ? avoids.join(", ") : (guest.avoid || []).join(", ")}
          </span>
        </Line>
      ) : null}

      {guest.circle ? <Line label={t("inspector.circle")}>{guest.circle}</Line> : null}
    </section>
  );
};

export default GuestInspector;
